import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Trophy } from "lucide-react";
import { Workout, ExerciseResult } from "@/schemas/workout";
import { formatTime } from "@/utils/formatTime";

interface CompleteWorkoutProps {
  workout: Workout | null;
  exerciseResults: Record<string, ExerciseResult>;
  elapsedTime: number;
  onCompleteWorkout: () => void;
}

export default function CompleteWorkout({
  workout,
  exerciseResults,
  elapsedTime,
  onCompleteWorkout,
}: CompleteWorkoutProps) {
  if (!workout) return null;

  return (
    <div className="container max-w-md mx-auto p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xl text-center">
            Workout Complete!
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 pb-6">
          <div className="text-center mb-6">
            <Trophy className="h-12 w-12 text-yellow-500 mx-auto mb-2" />
            <p className="text-lg font-medium mb-1">{workout.name}</p>
            <p className="text-sm text-gray-500 mb-4">
              Time: {formatTime(elapsedTime)}
            </p>
          </div>

          <div className="space-y-4 mb-6">
            <h3 className="font-medium">Exercise Summary</h3>
            {workout.exercises.map((exercise) => {
              const result = exerciseResults[exercise.id] || {};
              return (
                <div key={exercise.id} className="flex items-center">
                  <div className="mr-3">
                    {result.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border border-gray-300" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{exercise.name}</p>
                    <p className="text-sm text-gray-500">
                      {result.sets_completed || 0}/{result.total_sets || 0} sets
                      completed
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <Button onClick={onCompleteWorkout} size="lg" className="w-full">
            Save Results
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
